import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useMemo, useEffect, useCallback, useImperativeHandle } from 'react';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { getResourceNamePlaceholder } from '../../../../resources/utils/getResourceTitle.js';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { useQueryResourceRootSpaceSystemList } from '../../../../spaces/queries/useQueryResourceRootSpaceSystemList.js';
import { filterSpaces } from '../../../../spaces/utils/filterSpaces.js';
import { renderResourceIcon } from '../../../../resource-detail/components/ResourceIcon/renderResourceIcon.js';
import { ContextualMenuPluginKey } from '../ContextualMenuPlugin.js';
import { addExistingResource } from '../../EmbedResource/PasteDropPlugin.js';
import { EditorSelector } from '../../../Components/EditorSelector.js';
import { getFbcDocumentResourceId } from '../../FbcDocument.js';
import { useQueryResourceList } from '../../../../resources/queries/useQueryResourceList.js';

const ContextualLinkToItemMenu = /* @__PURE__ */ forwardRef(({ state: { query, view, selection }, onCandidatesChange }, ref) => {
  const debouncedQuery = useDebounce(query, 100);
  const [showingMoreItems, setShowingMoreItems] = useState(false);
  const [showingMoreSpaces, setShowingMoreSpaces] = useState(false);
  const itemParams = useMemo(() => ({
    name: debouncedQuery,
    limit: 15,
    includeRoots: false,
    ...debouncedQuery.length === 0 && {
      order: {
        property: "modifiedAt",
        direction: "DESC"
      }
    }
  }), [
    debouncedQuery
  ]);
  const { data: itemData, resources: itemResources, isLoading: isLoadingItems, fetchNextPage, isFetchingNextPage } = useQueryResourceList(itemParams, {
    meta: {
      ignoreResourceUpdate: true
    },
    placeholderData: void 0,
    enabled: true
  });
  const spaceParams = useMemo(() => ({
    keyword: debouncedQuery,
    sort: {
      property: "modifiedAt",
      direction: "DESC"
    }
  }), [
    debouncedQuery
  ]);
  const { spaceRoots, isLoading: isLoadingSpaces } = useQueryResourceRootSpaceSystemList(void 0, useMemo(() => ({
    includeInbox: true
  }), []));
  const spaceRootsFiltered = useMemo(() => filterSpaces(spaceRoots || [], spaceParams), [
    spaceParams,
    spaceRoots
  ]);
  const { hasSelf, resources } = useMemo(() => {
    const processed = showingMoreItems ? itemResources ?? [] : itemResources?.slice(0, 5);
    const resourceId = getFbcDocumentResourceId(view.state);
    const hasSelf2 = processed.findIndex((resource) => resource.id === resourceId);
    return {
      hasSelf: hasSelf2 !== -1,
      resources: hasSelf2 !== -1 ? processed.filter((resource) => resource.id !== resourceId) : processed
    };
  }, [
    itemResources,
    showingMoreItems,
    view.state
  ]);
  const spaces = useMemo(() => {
    if (showingMoreSpaces) return spaceRootsFiltered;
    return spaceRootsFiltered.slice(0, 5);
  }, [
    spaceRootsFiltered,
    showingMoreSpaces
  ]);
  const restOfItems = itemData ? itemData.total - resources.length - (hasSelf ? 1 : 0) : 0;
  const restOfSpaces = spaceRootsFiltered.length - spaces.length;
  const candidates = useMemo(() => {
    return [
      ...resources.map((resource) => resource.id) ?? [],
      ...restOfItems > 0 ? [
        "moreItems"
      ] : [],
      ...spaces.map((space) => space.id) ?? [],
      ...restOfSpaces > 0 ? [
        "moreSpaces"
      ] : []
    ];
  }, [
    resources,
    restOfItems,
    restOfSpaces,
    spaces
  ]);
  useEffect(() => {
    onCandidatesChange(candidates);
  }, [
    candidates,
    onCandidatesChange
  ]);
  const showMoreItems = useCallback(() => {
    if (!showingMoreItems) {
      setShowingMoreItems(true);
      return;
    }
    if (restOfItems <= 0) return;
    fetchNextPage();
  }, [
    showingMoreItems,
    restOfItems,
    fetchNextPage
  ]);
  const onSelect = useCallback(async (value) => {
    if (value === "moreItems") {
      setTimeout(() => {
        showMoreItems();
      }, 10);
      return;
    }
    if (value === "moreSpaces") {
      setTimeout(() => {
        setShowingMoreSpaces(true);
      }, 10);
      return;
    }
    view.focus();
    const pluginState = ContextualMenuPluginKey.getState(view.state);
    if (!pluginState || pluginState.query === null || pluginState.active === null) return;
    const tr = view.state.tr;
    tr.deleteRange(pluginState.range.from, pluginState.range.to);
    view.dispatch(tr);
    await addExistingResource(view, value, pluginState.range.from);
  }, [
    view,
    showMoreItems
  ]);
  useImperativeHandle(ref, () => ({
    onSelect
  }));
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsxs(EditorSelector.Group, {
        children: [
          /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
            children: isLoadingItems ? /* @__PURE__ */ jsxs(Fragment, {
              children: [
                /* @__PURE__ */ jsx(Spinner, {
                  size: 16,
                  color: cssVar["color-text-tertiary"]
                }),
                "Loading items..."
              ]
            }) : resources.length > 0 ? "Items:" : "No items results"
          }),
          resources.map((resource) => {
            return /* @__PURE__ */ jsx(EditorSelector.Item, {
              onClick: () => onSelect(resource.id),
              "data-keyboard-selected": selection === resource.id,
              value: resource.id,
              label: `${resource.name || getResourceNamePlaceholder(resource)}${resource.extension ? `.${resource.extension}` : ""}`,
              icon: renderResourceIcon(resource, {
                size: 16
              })
            }, resource.id);
          }),
          restOfItems > 0 && /* @__PURE__ */ jsx(EditorSelector.MoreButton, {
            onClick: () => onSelect("moreItems"),
            "data-keyboard-selected": selection === "moreItems",
            value: "moreItems",
            label: `${restOfItems > 15 ? "15+" : restOfItems} more results`,
            "data-prevent-default": true,
            disabled: isFetchingNextPage
          })
        ]
      }),
      /* @__PURE__ */ jsxs(EditorSelector.Group, {
        children: [
          /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
            children: isLoadingSpaces || !spaceRootsFiltered ? /* @__PURE__ */ jsxs(Fragment, {
              children: [
                /* @__PURE__ */ jsx(Spinner, {
                  size: 16,
                  color: cssVar["color-text-tertiary"]
                }),
                "Loading spaces..."
              ]
            }) : spaceRootsFiltered.length > 0 ? "Spaces:" : "No spaces results"
          }),
          spaces.map((space) => {
            return /* @__PURE__ */ jsx(EditorSelector.Item, {
              onClick: () => onSelect(space.id),
              "data-keyboard-selected": selection === space.id,
              value: space.id,
              label: space.subtype === "inbox" ? "Inbox" : space.folder.name || "Untitled",
              // @ts-expect-error -- some issue with the folder type
              icon: renderResourceIcon(space.folder, {
                size: 16
              })
            }, space.id);
          }),
          restOfSpaces > 0 && /* @__PURE__ */ jsx(EditorSelector.MoreButton, {
            onClick: () => onSelect("moreSpaces"),
            "data-keyboard-selected": selection === "moreSpaces",
            value: "moreSpaces",
            label: `${restOfSpaces > 15 ? "15+" : restOfSpaces} more results`,
            "data-prevent-default": true
          })
        ]
      })
    ]
  });
});
ContextualLinkToItemMenu.displayName = "ContextualLinkToItemMenu";

export { ContextualLinkToItemMenu as default };
//# sourceMappingURL=ContextualLinkToItemMenu.js.map
